<script setup lang="ts">
/**
 * Multi-line text input.
 *
 * Mirrors AppInput's look (same border, focus ring, invalid styling)
 * but renders a `<textarea>` so descriptions and free-form notes can
 * span several lines without horizontal-scrolling friction.
 *
 * Defaults to 16px font for iOS no-zoom-on-focus and a 5-row min
 * height. `rows` overrides the min; vertical resize is allowed but
 * horizontal is locked off — the parent column controls the width.
 */
import { computed, useAttrs } from 'vue'

import { cn } from '@/shared/lib/cn'

interface Props {
  modelValue?: string | null
  invalid?: boolean
  rows?: number
}

withDefaults(defineProps<Props>(), {
  modelValue: '',
  invalid: false,
  rows: 5,
})

const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

const attrs = useAttrs()

const classes = computed(() =>
  cn(
    'flex w-full resize-y rounded-md bg-background text-foreground px-3 py-2 text-base md:text-sm',
    'border border-input',
    'placeholder:text-muted-foreground',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive',
    (attrs.class as string) ?? '',
  ),
)

function onInput(e: Event) {
  const target = e.target as HTMLTextAreaElement
  emit('update:modelValue', target.value)
}
</script>

<template>
  <textarea
    :value="modelValue ?? ''"
    :rows="rows"
    :aria-invalid="invalid || undefined"
    data-focus-visible-managed
    :class="classes"
    @input="onInput"
  />
</template>
